import { useQuery } from "@apollo/client";
import { SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { GET_PRODUCT_TYPE } from "../productQueries";
import SimpleProductForm from "./SimpleProductForm";
import AdvancedProductForm from "./AdvancedProductForm";
import { SheetScrollableContent } from "@/components/ui/sheet-scrollable-content";

/**
 * Interface defining the props for the ProductForm component
 * @interface ProductFormProps
 * @property {Object} data - The product data object
 * @property {string} data.id - Unique identifier for the product
 * @property {string} data.type - Type of the product (SIMPLE_PRODUCT or ADVANCED_PRODUCT)
 * @property {Function} [onDirtyChange] - Optional callback for form dirty state changes
 * @property {Function} [onRequestClose] - Optional callback for form close requests
 */
interface ProductFormProps {
  data: {
    id: string;
    type: string;
  };
  onDirtyChange?: (dirty: boolean) => void;
  onRequestClose?: () => void;
}

/**
 * A dynamic form component that renders different product forms based on the product type.
 * Supports two types of products: SIMPLE_PRODUCT and ADVANCED_PRODUCT.
 * Falls back to a placeholder UI for unimplemented product types.
 *
 * @param {ProductFormProps} props - The component props
 * @returns {JSX.Element} The appropriate product form based on the type
 */
export default function ProductForm({
  data,
  onDirtyChange,
  onRequestClose,
}: ProductFormProps) {
  // Extract product type from data
  const type = data?.type;

  // Render SimpleProductForm for SIMPLE_PRODUCT type
  if (type === "SIMPLE_PRODUCT") {
    return (
      <SimpleProductForm
        data={{ ...data }}
        onDirtyChange={onDirtyChange}
        onRequestClose={onRequestClose}
      />
    );
  }
  // Render AdvancedProductForm for ADVANCED_PRODUCT type
  if (type === "ADVANCED_PRODUCT") {
    return (
      <AdvancedProductForm
        data={{ ...data }}
        onDirtyChange={onDirtyChange}
        onRequestClose={onRequestClose}
      />
    );
  }
  // Fallback UI for unimplemented product types
  return (
    <SheetContent className="sheet-content flex flex-col">
      <SheetHeader className="flex-shrink-0">
        <SheetTitle>{type}</SheetTitle>
      </SheetHeader>
      <SheetScrollableContent>
        {type} product editing is not yet implemented.
      </SheetScrollableContent>
    </SheetContent>
  );
}
